<template>
  <div>
    <v-alert
      v-if="driftWarning"
      dense
      outlined
      type="warning"
      class="ma-2"
    >
      Device clock drift exceeds 5 minutes. Scheduled events may not fire at the expected time.
    </v-alert>

    <v-alert
      v-if="syncPendingWarning"
      dense
      outlined
      type="info"
      class="ma-2"
    >
      Schedule changes are saved in the cloud but not yet applied on the device.
    </v-alert>

    <v-alert
      v-if="loadError"
      dense
      outlined
      type="error"
      class="ma-2"
      dismissible
      @input="loadError = null"
    >
      {{ loadError }}
    </v-alert>

    <v-toolbar
      flat
      class="scheduler-toolbar"
      :extended="canEdit"
      extension-height="36"
    >
      <v-toolbar-title class="scheduler-toolbar__title flex-shrink-0">
        <v-icon size="32px">event</v-icon>
        Scheduler
      </v-toolbar-title>
      <v-spacer />
      <v-btn-toggle v-model="calendarType" mandatory dense class="mr-2 flex-shrink-0">
        <v-btn value="day">Day</v-btn>
        <v-btn value="week">Week</v-btn>
        <v-btn value="month">Month</v-btn>
      </v-btn-toggle>
      <timezone-selector
        v-model="displayTimezone"
        label="Timezone"
        dense
        hide-details
        show-selection-title
        class="scheduler-toolbar__timezone mr-2"
      />
      <v-btn
        icon
        title="Refresh events"
        class="mr-2 flex-shrink-0"
        :loading="loading"
        @click="refreshEvents()"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        v-if="canEdit"
        color="primary"
        class="flex-shrink-0"
        @click="openNewEvent"
      >
        <v-icon left>add</v-icon>
        New Event
      </v-btn>
      <template v-if="canEdit" #extension>
        <div class="scheduler-toolbar__hint caption text--secondary">
          Click an empty time slot on the calendar to create an event at that time.
        </div>
      </template>
    </v-toolbar>

    <v-divider />

    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <div
      v-if="deviceColorLegend.length > 1"
      class="scheduler-device-legend px-4 py-2"
    >
      <span class="caption text--secondary mr-2">Devices:</span>
      <v-chip
        v-for="item in deviceColorLegend"
        :key="item.device_guid"
        x-small
        class="mr-1 mb-1 scheduler-device-legend__chip"
        :style="{ backgroundColor: item.color, color: '#fff' }"
      >
        {{ item.device_name }}
      </v-chip>
    </div>

    <v-row>
      <v-col :cols="selectedEvent ? 8 : 12">
        <v-calendar
          ref="calendar"
          v-model="focus"
          :type="calendarType"
          :events="calendarEvents"
          :event-height="calendarEventHeight"
          :class="calendarClasses"
          @click:event="onEventClick"
          @click:more="onCalendarMoreClick"
          @click:time="onCalendarSlotClick"
          @click:day="onCalendarSlotClick"
          @click:date="onCalendarSlotClick"
        />
      </v-col>
      <v-col v-if="selectedEvent" cols="4">
        <scheduler-event-detail
          :event="selectedEvent"
          :can-edit="canEdit"
          @close="selectedEvent = null"
          @edit="openEditEvent"
          @deleted="refreshEvents"
          @error="onFormError"
        />
      </v-col>
    </v-row>

    <scheduler-event-form
      v-model="showEventForm"
      :event="editingEvent"
      :initial-schedule="createScheduleSeed"
      :scope="scope"
      :org-guid="organization && organization.org_guid"
      :fleet-guid="fleetGuid"
      :default-device-guid="defaultDeviceGuid"
      :can-edit="canEdit"
      @saved="onEventSaved"
      @error="onFormError"
    />
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'
import {
  expandSchedulerEventsForCalendar,
  recurrenceSummary,
} from '../../utils/schedulerRecurrence'
import TimezoneSelector from '../common/TimezoneSelector.vue'
import SchedulerEventForm from './SchedulerEventForm.vue'
import SchedulerEventDetail from './SchedulerEventDetail.vue'

export default {
  name: 'SchedulerCalendar',

  components: {
    TimezoneSelector,
    SchedulerEventForm,
    SchedulerEventDetail,
  },

  props: {
    scope: {
      type: String,
      required: true,
    },
    fleetGuid: {
      type: String,
      default: null,
    },
    fleet: {
      type: Object,
      default: null,
    },
    deviceGuid: {
      type: String,
      default: null,
    },
    deviceName: {
      type: String,
      default: null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      events: [],
      focus: new Date().toISOString().substr(0, 10),
      calendarType: 'week',
      selectedEvent: null,
      showEventForm: false,
      editingEvent: null,
      createScheduleSeed: null,
      driftWarning: false,
      syncPendingWarning: false,
      loadError: null,
      displayTimezone: null,
      refreshTimer: null,
      refreshIntervalMs: 30000,
    }
  },

  computed: {
    ...mapGetters('user', ['organization']),
    ...mapGetters('userPreferences', ['displayTimezonePref']),

    defaultDeviceGuid() {
      if (this.scope === 'device' && this.deviceGuid) {
        return this.deviceGuid
      }
      if (this.scope === 'fleet' && this.fleet && this.fleet.devices && this.fleet.devices.length) {
        return this.fleet.devices[0].device_guid
      }
      return null
    },

    showDeviceOnEvents() {
      return this.scope === 'org' || this.scope === 'fleet'
    },

    deviceColorByGuid() {
      const map = {}
      if (!this.showDeviceOnEvents) {
        return map
      }
      for (const evt of this.events) {
        const guid = evt.device_guid
        if (!guid || map[guid]) {
          continue
        }
        map[guid] = this.colorForDeviceGuid(guid)
      }
      return map
    },

    deviceColorLegend() {
      return Object.keys(this.deviceColorByGuid)
        .map((guid) => {
          const match = this.events.find((evt) => evt.device_guid === guid)
          return {
            device_guid: guid,
            device_name: (match && match.device_name) || guid,
            color: this.deviceColorByGuid[guid],
          }
        })
        .sort((a, b) => a.device_name.localeCompare(b.device_name))
    },

    calendarWindowStart() {
      const tz = this.effectiveTimezone
      return moment.tz(tz).subtract(30, 'days').startOf('day')
    },

    calendarWindowEnd() {
      const tz = this.effectiveTimezone
      return moment.tz(tz).add(365, 'days').endOf('day')
    },

    calendarEvents() {
      const monthView = this.calendarType === 'month'
      const colors = this.deviceColorByGuid
      const tz = this.effectiveTimezone
      const expanded = expandSchedulerEventsForCalendar(
        this.events,
        this.calendarWindowStart,
        this.calendarWindowEnd,
        tz
      )
      const mapped = []
      for (const { event: evt, instance, recurring } of expanded) {
        const start = instance.start.format('YYYY-MM-DD HH:mm:ss')
        const end = instance.end.format('YYYY-MM-DD HH:mm:ss')
        const startDate = new Date(start)
        const endDate = new Date(end)
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          continue
        }
        const color = this.showDeviceOnEvents && evt.device_guid
          ? colors[evt.device_guid] || 'primary'
          : 'primary'
        let label = evt.name
        if (recurring) {
          label += ' (series)'
        }
        if (this.showDeviceOnEvents && evt.device_name) {
          label += ' (' + evt.device_name + ')'
        }
        mapped.push({
          name: label,
          start: startDate,
          end: endDate,
          color,
          timed: !monthView,
          _raw: {
            ...evt,
            _occurrenceStart: instance.startUtc,
            _occurrenceEnd: instance.endUtc,
            _recurrenceSummary: recurrenceSummary(
              evt.series_recurrence || evt.recurrence
            ),
          },
        })
      }
      return mapped
    },

    calendarEventHeight() {
      return this.calendarType === 'month' ? 22 : 20
    },

    calendarClasses() {
      return {
        'scheduler-calendar--editable': this.canEdit,
        'scheduler-calendar--month': this.calendarType === 'month',
      }
    },

    effectiveTimezone() {
      return this.displayTimezone || this.displayTimezonePref || this.$helpers.getBrowserTimezone()
    },
  },

  watch: {
    displayTimezone(val) {
      this.$store.dispatch('userPreferences/setDisplayTimezone', val)
    },
    calendarType() {
      this.$nextTick(() => {
        const cal = this.$refs.calendar
        if (cal && typeof cal.updateEventVisibility === 'function') {
          cal.updateEventVisibility()
        }
      })
    },
    fleetGuid() {
      this.refreshEvents()
    },
    deviceGuid() {
      this.refreshEvents()
    },
    showEventForm(open) {
      if (!open) {
        this.createScheduleSeed = null
      }
    },
  },

  mounted() {
    this.displayTimezone = this.displayTimezonePref || this.$helpers.getBrowserTimezone()
    this.refreshEvents()
    document.addEventListener('visibilitychange', this.onPageVisibilityChange)
  },

  beforeDestroy() {
    this.clearRefreshTimer()
    document.removeEventListener('visibilitychange', this.onPageVisibilityChange)
  },

  methods: {
    colorForDeviceGuid(deviceGuid) {
      let hash = 0
      for (let i = 0; i < deviceGuid.length; i += 1) {
        hash = ((hash << 5) - hash) + deviceGuid.charCodeAt(i)
        hash |= 0
      }
      const hue = Math.abs(hash) % 360
      const saturation = 58 + (Math.abs(hash >> 8) % 22)
      const lightness = 40 + (Math.abs(hash >> 16) % 14)
      return 'hsl(' + hue + ', ' + saturation + '%, ' + lightness + '%)'
    },

    pageIsActive() {
      return document.visibilityState === 'visible' && document.hasFocus()
    },

    clearRefreshTimer() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    scheduleRefresh() {
      this.clearRefreshTimer()
      this.refreshTimer = setTimeout(() => {
        this.pollRefresh()
      }, this.refreshIntervalMs)
    },

    pollRefresh() {
      if (this.pageIsActive()) {
        this.refreshEvents({ silent: true })
      } else {
        this.scheduleRefresh()
      }
    },

    onPageVisibilityChange() {
      if (this.pageIsActive()) {
        this.refreshEvents({ silent: true })
      }
    },

    async refreshEvents(options = {}) {
      const silent = options && options.silent === true
      if (!silent) {
        this.loading = true
      }
      this.driftWarning = false
      this.syncPendingWarning = false
      if (!silent) {
        this.loadError = null
      }
      const selectedKey =
        this.selectedEvent && this.$scheduler.eventKey(this.selectedEvent)
      try {
        if (this.scope === 'org') {
          const orgGuid = this.organization && this.organization.org_guid
          this.events = await this.$scheduler.loadOrgEvents(orgGuid)
        } else if (this.scope === 'device' && this.deviceGuid) {
          const name = this.deviceName || this.deviceGuid
          this.events = await this.$scheduler.loadDeviceEvents(this.deviceGuid, name)
        } else if (this.fleet) {
          this.events = await this.$scheduler.loadFleetEvents(this.fleet)
        }
        for (const evt of this.events) {
          if (evt.clock_drift_exceeded) {
            this.driftWarning = true
          }
          if (evt.scheduler_sync_pending) {
            this.syncPendingWarning = true
          }
        }
        if (selectedKey) {
          const updated = this.events.find(
            (evt) => this.$scheduler.eventKey(evt) === selectedKey
          )
          this.selectedEvent = updated || null
        }
      } catch (err) {
        console.error('Scheduler refresh failed', err)
        if (!silent) {
          this.loadError = this.$helpers.parseError(err)
        }
        this.events = []
      } finally {
        if (!silent) {
          this.loading = false
        }
        this.scheduleRefresh()
      }
    },

    onFormError(message) {
      this.loadError = message
    },

    onEventClick({ event, nativeEvent }) {
      if (nativeEvent) {
        nativeEvent.stopPropagation()
      }
      this.showEventForm = false
      this.createScheduleSeed = null
      this.editingEvent = null
      const payload = event && event._raw ? event : (event || {})
      this.selectedEvent = payload._raw || payload
    },

    wasCalendarEventClick(nativeEvent) {
      if (!nativeEvent || !nativeEvent.target) {
        return false
      }
      let el = nativeEvent.target
      while (el) {
        if (el.classList) {
          if (
            el.classList.contains('v-event')
            || el.classList.contains('v-event-timed')
            || el.classList.contains('v-event-more')
          ) {
            return true
          }
        }
        el = el.parentElement
      }
      return false
    },

    onCalendarMoreClick(day) {
      const dateStr = day && day.date ? day.date.substr(0, 10) : null
      if (!dateStr) {
        return
      }
      const matches = this.eventsOnDate(dateStr)
      if (matches.length) {
        this.selectedEvent = matches[0]
      }
    },

    eventsOnDate(dateStr) {
      const tz = this.effectiveTimezone
      const dayStart = moment.tz(dateStr, 'YYYY-MM-DD', tz).startOf('day')
      const dayEnd = dayStart.clone().endOf('day')
      return this.events.filter((evt) => {
        const start = moment.utc(evt.start_time).tz(tz)
        const end = moment.utc(evt.end_time).tz(tz)
        return start.isBefore(dayEnd) && end.isAfter(dayStart)
      })
    },

    roundToNearest15Minutes(momentValue) {
      const m = momentValue.clone().seconds(0).milliseconds(0)
      const roundedMinutes = Math.round(m.minutes() / 15) * 15
      if (roundedMinutes === 60) {
        return m.add(1, 'hour').minutes(0)
      }
      return m.minutes(roundedMinutes)
    },

    onCalendarSlotClick(day, nativeEvent) {
      if (!this.canEdit || !day) {
        return
      }
      if (this.wasCalendarEventClick(nativeEvent)) {
        return
      }
      const tz = this.effectiveTimezone
      let m
      if (day.date) {
        m = moment.tz(day.date.substr(0, 10), 'YYYY-MM-DD', tz)
      } else {
        m = moment.tz(tz).year(day.year).month(day.month - 1).date(day.day)
      }
      if (day.hasTime && typeof day.hour === 'number') {
        m.hour(day.hour).minute(day.minute || 0)
      } else {
        m.hour(9).minute(0)
      }
      m = this.roundToNearest15Minutes(m)
      this.createScheduleSeed = {
        startDate: m.format('YYYY-MM-DD'),
        startTime: m.format('HH:mm'),
        durationMinutes: 60,
      }
      this.focus = m.format('YYYY-MM-DD')
      this.selectedEvent = null
      this.editingEvent = null
      this.showEventForm = true
    },

    openNewEvent() {
      this.createScheduleSeed = null
      this.editingEvent = null
      this.showEventForm = true
    },

    openEditEvent(evt) {
      this.createScheduleSeed = null
      this.editingEvent = evt
      this.showEventForm = true
    },

    onEventSaved(response) {
      if (response && response.clock_drift_exceeded) {
        this.driftWarning = true
      }
      this.createScheduleSeed = null
      this.showEventForm = false
      this.refreshEvents()
    },
  },
}
</script>

<style scoped>
.scheduler-toolbar ::v-deep .v-toolbar__content {
  flex-wrap: nowrap;
  min-height: 56px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.scheduler-toolbar ::v-deep .v-toolbar__extension {
  padding: 0 16px 10px;
}

.scheduler-toolbar__hint {
  line-height: 1.4;
}

.scheduler-toolbar__timezone {
  flex: 0 0 260px;
  width: 260px;
  max-width: 260px;
}

.scheduler-toolbar__timezone ::v-deep .v-input__control {
  min-height: 36px !important;
}

.scheduler-toolbar__timezone ::v-deep .v-input__slot {
  min-height: 36px !important;
}

.scheduler-toolbar__timezone ::v-deep .v-select__selection-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scheduler-calendar--editable ::v-deep .v-calendar-daily__day,
.scheduler-calendar--editable ::v-deep .v-calendar-weekly__day {
  cursor: pointer;
}

/* Month view uses the weekly grid; give cells room for event bars. */
.scheduler-calendar--month ::v-deep .v-calendar-weekly__day {
  min-height: 116px;
  vertical-align: top;
}

.scheduler-calendar--month ::v-deep .v-calendar-weekly__week {
  flex: 1 1 auto;
}

.scheduler-calendar--month ::v-deep .v-event,
.scheduler-calendar--month ::v-deep .v-event-more {
  font-size: 12px;
  line-height: 20px;
}

.scheduler-calendar--month ::v-deep .v-event-summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.scheduler-device-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
</style>
